<template>
  <div>
    <PageHeader />
    <ProjInfo :projectInfo="projectInfo" :changelog="changelog" />

        <v-container class="section-block">

            <v-row>
                <v-col class="col-12 col-md-8">
                    <v-responsive :aspect-ratio="16/9">
                        <video width="100%" :src="video.tutorial" :poster="images.poster" controls></video>
                    </v-responsive>
                </v-col>
                <v-col class="col-12 col-md-4">
                    <h2>Walkthrough</h2>
                    <p>
                        A quick look at how to work with the dots in a device flow. 
                    </p>
                    <p>Quick and simple. </p>
                </v-col>
            </v-row>

            <v-row>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <div class=" group-header">Usage</div>
                </v-col>
                <v-col class="col-12 col-md-8"> </v-col>
            <!-- </v-row>
            <v-row> -->
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.newLayer" width="100%" alt="">
                    <h2>Create a new Dotkit layer</h2>
                    <p>
                        This shape layer contains everything necessary to work with dots and the custom shapes they form. There are no precomps or folders to manage.
                    </p>
                    <p>
                        This shape layer is self-contained and there is no risk of confusing the system. Feel free to trim, split, duplicate as needed.
                    </p>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <!-- <video width="100%" src="/video/Dotkit-Custom.mp4" autoplay loop muted></video> -->
                    <img :src="images.addState" width="100%" alt="">
                    <h2>Add states</h2>
                    <p>
                        There are <a href="#states">6 discrete states</a> and clicking one of the buttons will add a named marker to the Dotkit layer at the playhead –or the selected layer(s) if there are multiple Dotkit layers in the comp. 
                    </p>
                    <p>
                        These markers tell the Dotkit extension about timing and where to dynamically create keyframes. Adding a new state will create a marker and update the keyframes.
                    </p>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <video width="100%" :src="video.updateKeyframes" autoplay loop muted></video>
                    <h2>Update keyframes</h2>
                    <p>
                        The realtime updates of v1 have been removed in favor of keyframes because they were slow and inaccurate. After retiming the <i>state markers</i>, click <b>update Keyframes</b> to rebuild all the keyframes on the Dotkit layer.
                    </p>
                    <p>
                        All keyframes controling the animation of the dots (not layer transforms) will be deleted and new state and <a href="#automatic transitions">transition</a> keys will be added. 
                    </p>
                </v-col>
            </v-row>

            <v-row>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <div class=" group-header">Edit states</div>
                </v-col>
                <v-col class="col-12 col-md-8"> </v-col>
                <v-col class="col-12 col-md-6">
                    <p>
                        A state is just a layer marker. It may be dragged forward and backward in time. But as you start adding more states it may become difficult to retime all of them. 
                    </p>
                    <p>
                        These buttons are here for support. 
                    </p>
                </v-col>
                <v-col class="col-12 col-md-6"> </v-col>

                <v-col class="col-12 col-sm-6 col-md-4">
                    <video width="100%" :src="video.shiftStates" autoplay loop muted></video>
                    <h2>Shift states</h2>
                    <p>
                        Once you have a sequence of state markers in your timeline it will be difficult to adjust everything if you need to retime something at the beginning. 
                        
                    </p>
                    <p>
                        Clicking these buttons will move all markers after the nearest marker (see Remove Nearest) forward/backward in time the nubber of frames you specify, then <b>Update Keyframes</b>
                    </p>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <img :src="images.removeNearest" width="100%" alt="">
                    <h2>Remove nearest</h2>
                    <p>
                        After Effects has a weird system of identifying keyframes and markers by their relation to the playhead. 
                        Move the playhead near one of the markers and click to remove it from the layer and update the keys. 
                    </p>
                    <p>
                        This is a bit more automated than <b>Right click > Delete this Marker</b>, then clicking <b>Update Keyframes</b>.
                    </p>
                </v-col>
                <v-col class="col-12 col-sm-6 col-md-4">
                    <video width="100%" :src="video.clearAll" autoplay loop muted></video>
                    <h2>Clear all</h2>
                    <p>
                        Sometimes a Dotkit layer becomes too crowded. You could always delete it and create another one. But if you have the layer positioned just right in the comp and need a fresh start. 
                    </p>
                    <p>
                        This is a bit more automated than <b>Right click > Delete all Marker</b>, then clicking <b>Update Keyframes</b>.
                    </p>
                </v-col>
            </v-row>

            <v-row>
                <v-col class="col-12 col-md-7">
                    <v-img :src="images.transitions"></v-img>
                </v-col>
                <v-col class="col-12 col-md-5">
                    <h2 style="margin-top: 0">Automatic transitons</h2>
                    <p>
                        One of the areas that v1 failed to handle properly was to transitioning between states. It was automatic, but it was little more than a crossfade, and but rarely accurate.
                    </p>
                    <p>
                        The new transition system will now select the appropriate keyframe set to move between states. Kind of like a blend system in a game engine.
                    </p>
                    <br />
                    <br />
                    <h3>Some transtions don't work</h3>
                    <p>
                        The Mic state is unique in that it <a href="#no transition">does not have a transition</a> between Super G and Molecule states. 
                    </p>
                </v-col>
            </v-row>
        </v-container>

        
        <v-container class="section-block">
        <v-row>
            <v-col class="col-12 col-sm-6 col-md-4">
                <div class=" group-header">Included states</div>
            </v-col>
        </v-row>
            
        <div v-for="(type, typeIndex) in dotStates" :key="typeIndex" class="state-box">
            <h2 :id="typeIndex">{{ typeIndex | uppercase }}</h2>
            <v-row>
                <v-col v-for="(state, stateIndex) in type" :key="stateIndex" class="col-6 col-sm-4 col-md-3" no-gutters>
                    <img :src="require(`../assets/gif/dotkit2/${state}.gif`)" style="width: inherit" :alt="state">
                    <h4>{{ state | removeUnderscores }}</h4>
                </v-col>
            </v-row>
        </div>
    </v-container>

    
    <!-- <InstallInfo :projectInfo="projectInfo" /> -->
    <InstallInfo id="install" name="Dotkit" extension :hosts="['After Effects']"/>

    <Changelog :changelog="changelog" /> 

  </div>
</template>

<script>


export default {
    name: 'dotkit',
    components: {
        // PageHeader,
        // ProjInfo,
        // InstallInfo,
    },
    data: () => ({
        images: {
            poster: require('../assets/images/DotkitPoster.jpg'),
            transitions: require('../assets/images/Dotkit_transtions.png'),
            newLayer: require('../assets/images/Dotkit-NewLayer.jpg'),
            addState: require('../assets/images/Dotkit-AddState.jpg'),
            removeNearest: require('../assets/images/Dotkit-RemoveNearest.jpg'),
        },
        video: {
            tutorial: require('../assets/video/Dotkit2.mp4'),
            updateKeyframes: require('../assets/video/Dotkit-UpdateKeys.mp4'),
            shiftStates: require('../assets/video/Dotkit-ShiftStates.mp4'),
            clearAll: require('../assets/video/Dotkit-ClearAll.mp4'),
        },
        projectInfo: {
            name: 'Dotkit',
            subtitle: 'Animating the dots with states, not keyframes',
            source: true,
            // download: '/download/DotKit_1.2.zip',
            // version: '2.0',
            // updated: 'Sep 7, 2021',
            about: `This is 2.0 of the universally loved <a href="dotkit-v1">DotKit v1</a> with some major updates.

            Expressions and precomps have been replaced with a single shape layer and a dynamic keyframing system. This reduces render time in a big way, improves flexibility to using the dots like a normal layer, and adds support for <a href="https://airbnb.design/lottie/">Lottie</a>.`
        },
        changelog: [
            {
                version: '2.0.1',
                date: 'Sept 17, 2021',
                added: [
                    'Dot rotation thinking state',
                ],
                changed: [
                    'Renamed Molecule sine wave to Molecule Thinking',
                    'Smooth out Molecule Thinking transitions'
                ]
            },
            {
                version: '2.0.0',
                date: 'Sept 7, 2021',
                comment: [
                    'Initial release',
                ],
            },
        ],
        dotStates: {
            states: [
                'Listening',
                'Thinking',
                'User_input',
                'Mic',
                'Molecule',
                'Molecule_Thinking',
                'Super_G',
            ],
            'automatic transitions': [
                'Listening_to_thinking',
                'Listening_to_user_input',
                'Listening_to_mic',
                'Listening_to_molecule_thinking',
                'Listening_to_molecule',
                'User_input_to_Mic',
                'User_input_to_Molecule',
                'Molecule_to_thinking',
                'Thinking_to_molecule_thinking',
                'Listening_to_G',
                'Molecule_to_G',
            ],
            'no transition': [
                'Molecule_to_mic',
                'Mic_to_G',
            ],
        }
    }),
    filters: {
        uppercase(str) {
            return str.toUpperCase()
        },
        removeUnderscores(str) {
            return str.replace(/_/g, ' ')
        },
    }
}
</script>

<style scoped>
/* .state-box {
    background: #fff;
    margin: 16px 12px;
    padding: 16px 0;
}
.state-box h3 {
    padding-left: 16px;
} */
.state-box img {
    border: 1px solid rgba(0,0,0,0.03);
}
.state-box .col {
    margin-bottom: 18px;
}
.state-box h2 {
    /* margin-bottom: -54px; */
}

</style>